import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type {
    CatalogOtherCostVariableTypeEnum,
    IsUniqueVariableNameQueryVariables,
    ProjectMethodologyEnum,
} from 'gql/graphql';
import { useDebounce } from 'uikit';

import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import type { VariableIdType } from '../../types';
import { isUniqueVariableNameGql } from '../catalogVariablesQueries';

interface Props {
    id: Exclude<VariableIdType, undefined>;
    name: string | undefined;
    variableType: CatalogOtherCostVariableTypeEnum;
    catalogUniqueId: string;
    globalOnly: boolean;
    methodology: ProjectMethodologyEnum;
}

export const useIsUniqueVariableName = ({
    catalogUniqueId,
    id,
    name,
    variableType,
    globalOnly,
    methodology,
}: Props) => {
    const dispatch = useDispatch();

    const [resetIsUniqVariableName] = useResetStoreData({ requestId: 'isUniqVariableName' });

    useEffect(
        () => () => {
            resetIsUniqVariableName(undefined);
        },
        [resetIsUniqVariableName],
    );

    const [isUniqueVariableNameData, isUniqueVariableNameStatus, , isUniqueVariableNameRequest] = useRequest({
        requestId: 'isUniqVariableName',
        query: isUniqueVariableNameGql,
        withEffect: false,
    });

    const debounceRequest = useDebounce(() => {
        if (name) {
            const variables: IsUniqueVariableNameQueryVariables = {
                catalogUniqueId,
                variableName: name.trim(),
                variableType,
                globalOnly,
                methodology,
            };

            if (id) {
                variables.variableId = id;
            }

            dispatch(isUniqueVariableNameRequest(variables));
        }
    }, 300);

    useEffect(() => {
        if (name?.length) {
            debounceRequest();
        }
    }, [debounceRequest, name?.length, variableType]);

    return useMemo(
        () => ({
            isUniqueVariableNameData: isUniqueVariableNameData?.isUniqueVariableName.result,
            isUniqueVariableNameStatus,
        }),
        [isUniqueVariableNameData?.isUniqueVariableName.result, isUniqueVariableNameStatus],
    );
};
